import {Banner} from '@primer/react'
import {clsx} from 'clsx'
import type {CreateAPITaskParams, CreateAPITaskMutationParams} from '../types/api-task'
import {truncateLines} from '../utils/truncate-lines'
import styles from './TaskCreationFailedBanner.module.css'

export const CUSTOMER_SELECTION_REQUIRED_ERROR = 'customer selection required'
export const COPILOT_SETTINGS_PATH = '/settings/copilot/features'
export const CUSTOMER_SELECTION_REQUIRED_DESCRIPTION =
  'Before you can use Copilot cloud agent, you need to pick a "Usage billed to" option in your Copilot settings.'

function isCustomerSelectionRequiredError(errorMessage?: string): boolean {
  return errorMessage?.toLowerCase().includes(CUSTOMER_SELECTION_REQUIRED_ERROR) ?? false
}

/**
 * Represents a failed task creation attempt using the new task API that can be retried.
 * Extends CreateAPITaskMutationParams so that all the original task creation parameters
 * (including clientGeneratedId) are preserved from the onError callback.
 */
export type FailedAPITaskCreationAttempt = CreateAPITaskMutationParams & {
  errorMessage: string
  timestamp: string
}

export type UIFailedTaskCreationAttempt = Pick<
  FailedAPITaskCreationAttempt,
  'problemStatement' | 'errorMessage' | 'timestamp'
>

/**
 * Extracts task creation parameters from a failed API task attempt,
 * removing the error-specific fields (errorMessage, timestamp) and clientGeneratedId
 * so that a new ID will be generated for the retry.
 */
export function getAPITaskParamsFromAttempt(attempt: FailedAPITaskCreationAttempt): CreateAPITaskParams {
  const {errorMessage, timestamp, clientGeneratedId, ...taskParams} = attempt
  return taskParams
}

interface TaskCreationFailedBannerProps {
  problemStatement?: string
  errorMessage?: string
  handleRetry: () => void
  handleDismiss: () => void
}

export function TaskCreationFailedBanner({
  problemStatement,
  errorMessage,
  handleRetry,
  handleDismiss,
}: TaskCreationFailedBannerProps) {
  if (isCustomerSelectionRequiredError(errorMessage)) {
    return (
      <Banner
        variant="critical"
        title="Task Creation Failed"
        hideTitle
        description={CUSTOMER_SELECTION_REQUIRED_DESCRIPTION}
        primaryAction={
          <Banner.PrimaryAction as="a" href={COPILOT_SETTINGS_PATH}>
            Settings
          </Banner.PrimaryAction>
        }
        secondaryAction={<Banner.SecondaryAction onClick={handleDismiss}>Dismiss</Banner.SecondaryAction>}
      />
    )
  }

  const truncatedProblemStatement = truncateLines(problemStatement ?? '', 20, 1)
  const baseMessage = `Unable to start task for '${truncatedProblemStatement}'`

  const description = errorMessage ? `${baseMessage}: ${errorMessage}` : `${baseMessage}. Please try again.`

  return (
    <Banner
      variant="critical"
      title="Task Creation Failed"
      hideTitle
      description={description}
      secondaryAction={<Banner.SecondaryAction onClick={handleDismiss}>Dismiss</Banner.SecondaryAction>}
      primaryAction={<Banner.PrimaryAction onClick={handleRetry}>Retry</Banner.PrimaryAction>}
    />
  )
}

interface TaskCreationFailedBannerListProps {
  attempts: UIFailedTaskCreationAttempt[]
  onRetry: (index: number) => void
  onDismiss: (index: number) => void
  className?: string
}

export function TaskCreationFailedBannerList({
  attempts,
  onRetry,
  onDismiss,
  className,
}: TaskCreationFailedBannerListProps) {
  if (attempts.length === 0) {
    return null
  }

  return (
    <div className={clsx(styles.bannerList, className)}>
      {attempts.map((attempt, index) => (
        <TaskCreationFailedBanner
          key={`${attempt.problemStatement}-${attempt.timestamp}`}
          problemStatement={attempt.problemStatement}
          errorMessage={attempt.errorMessage}
          handleRetry={() => onRetry(index)}
          handleDismiss={() => onDismiss(index)}
        />
      ))}
    </div>
  )
}
